import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import type { ReactNode } from "react";

/**
 * Controlled dialog on Base UI: focus trap, Escape, scroll lock and the inert background
 * come from the library, which is exactly what hand-rolled modals get wrong.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  size = "md",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  size?: "md" | "lg" | "xl";
}) {
  return (
    <BaseDialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className="fixed inset-0 z-40 bg-[#1f2544]/50 transition-opacity duration-200 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0" />
        <BaseDialog.Viewport className="fixed inset-0 z-50 grid place-items-end overflow-y-auto sm:place-items-center sm:p-6">
          <BaseDialog.Popup
            className={`w-full rounded-t-[2rem] bg-surface text-ink shadow-2xl transition duration-200 outline-none data-[ending-style]:translate-y-6 data-[ending-style]:opacity-0 data-[starting-style]:translate-y-6 data-[starting-style]:opacity-0 sm:rounded-[2rem] ${
              size === "xl" ? "sm:max-w-4xl" : size === "lg" ? "sm:max-w-2xl" : "sm:max-w-md"
            }`}
          >
            <div className="grid gap-5 p-6 pb-8 sm:p-8">
              <header className="flex items-start justify-between gap-4">
                <div className="grid gap-1">
                  <BaseDialog.Title className="text-2xl leading-tight font-semibold">
                    {title}
                  </BaseDialog.Title>
                  {description && (
                    <BaseDialog.Description className="text-muted">
                      {description}
                    </BaseDialog.Description>
                  )}
                </div>
                <BaseDialog.Close
                  className="btn-ghost -mt-1 -mr-2 size-11 text-2xl leading-none"
                  aria-label="Fermer"
                >
                  ×
                </BaseDialog.Close>
              </header>
              {children}
            </div>
          </BaseDialog.Popup>
        </BaseDialog.Viewport>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}
